import { useEffect, useState } from 'react'
import { Radio } from 'lucide-react'
import { useWaveStore } from '../../../stores/wave'
import { CITIES, P_WAVE_KM_S, S_WAVE_KM_S, WAVE_MAX_RADIUS_KM } from '../../../lib/seismic'
import { haversineKm } from '../../../lib/geo'
import { cn } from '../../../lib/utils'

const fmt = (sec: number) => (sec >= 60 ? `${Math.floor(sec / 60)}m${Math.round(sec % 60)}s` : `${sec.toFixed(1)}s`)

/**
 * P/S 波到达倒计时（地图 overlay）：预警触发后显示主要城市 S 波预计到达时间，
 * 到达后转红。波扫出最大半径后随 wave 结束自动隐藏。
 */
export function WaveArrivals() {
  const wave = useWaveStore((s) => s.wave)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!wave) return
    const timer = setInterval(() => setNow(Date.now()), 250)
    return () => clearInterval(timer)
  }, [wave])

  if (!wave) return null

  const elapsedS = Math.max(0, (now - wave.originTs) / 1000)
  const pRadius = Math.min(WAVE_MAX_RADIUS_KM, elapsedS * P_WAVE_KM_S)
  const sRadius = Math.min(WAVE_MAX_RADIUS_KM, elapsedS * S_WAVE_KM_S)
  const arrivals = CITIES.map((c) => {
    const dist = haversineKm(wave.lat, wave.lon, c.lat, c.lon)
    return { ...c, dist, sArrival: dist / S_WAVE_KM_S, pArrival: dist / P_WAVE_KM_S }
  })
    .filter((c) => c.dist <= WAVE_MAX_RADIUS_KM)
    .sort((a, b) => a.sArrival - b.sArrival)
    .slice(0, 8)

  return (
    <div className="panel-glass absolute bottom-3 left-3 z-10 w-64 rounded-lg p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          <Radio size={12} className="animate-pulse text-critical" /> 波传播 · 倒计时
        </div>
        <span className="font-mono text-[10px] text-muted-foreground">
          P {Math.round(pRadius)}km · S {Math.round(sRadius)}km
        </span>
      </div>
      <div className="space-y-1">
        {arrivals.map((c) => {
          const remaining = c.sArrival - elapsedS
          const arrived = remaining <= 0
          return (
            <div key={c.name} className="flex items-center gap-2 text-[11px]">
              <span className={cn('w-14 shrink-0', arrived ? 'text-critical font-medium' : 'text-foreground')}>{c.name}</span>
              <div className="relative h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-foreground/[.06]">
                <div
                  className={cn('absolute inset-y-0 left-0 rounded-full', arrived ? 'bg-critical' : 'bg-watch')}
                  style={{ width: `${Math.min(100, (elapsedS / c.sArrival) * 100)}%` }}
                />
              </div>
              <span className={cn('w-16 shrink-0 text-right font-mono', arrived ? 'text-critical' : 'text-muted-foreground')}>
                {arrived ? '已到达' : `-${fmt(remaining)}`}
              </span>
            </div>
          )
        })}
      </div>
      <p className="mt-2 border-t border-border pt-1.5 text-[10px] leading-4 text-muted-foreground">
        M{wave.magnitude.toFixed(1)} · 深源 S 波 3.5km/s · 倒计时基于直线距离估算
      </p>
    </div>
  )
}
